/* eslint-disable @angular-eslint/prefer-inject */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { LANGUAGES } from '@core/constants/language';
import { Language } from '../../core/interfaces/language';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  /**
   * @constructor
   * @param {TranslateService} _translate
   * @param {CookieService} _cookie
   */
  constructor(
    private _translate: TranslateService,
    private _cookie: CookieService,
  ) {
    this.registerSupportedLanguages();
    this._translate.setDefaultLang('en');
  }

  /**
   * Registra los idiomas soportados por la aplicación.
   */
  private registerSupportedLanguages(): void {
    const Languages: string[] = LANGUAGES.map(({ lang }: Language) => lang);
    this._translate.addLangs(Languages);
  }

  /**
   * Resuelve el idioma final usando cookie/navegador y valida que sea soportado.
   * @param {string} preferredLang
   * @returns {string}
   */
  private resolveLanguage(preferredLang?: string): string {
    const SupportedLangs: string[] = LANGUAGES.map(({ lang }: Language) => lang);
    const isCookieLang: boolean = this._cookie.check('lang');
    const rawLang: string =
      preferredLang ||
      (isCookieLang ? this._cookie.get('lang') : this._translate.getBrowserLang()) ||
      this._translate.getDefaultLang() ||
      'en';

    return SupportedLangs.includes(rawLang) ? rawLang : 'en';
  }

  /**
   * Inicializa el idioma del sitio web y espera a que la traducción esté cargada.
   * @returns {Promise<void>}
   */
  public async initializeLanguage(): Promise<void> {
    const Lang: string = this.resolveLanguage();
    await firstValueFrom(this._translate.use(Lang));
    this._cookie.set('lang', Lang);
  }

  /**
   * Retorna el idioma actual del sitio.
   * @returns {string}
   */
  public get getDefaultLang(): string {
    return this.resolveLanguage(this._translate.currentLang);
  }

  /**
   * Asigna el idioma seleccionado y lo guarda en las cookies.
   * @param {string} lang
   */
  public set setLanguage(lang: string) {
    const Lang: string = this.resolveLanguage(lang);
    this._cookie.set('lang', Lang);
    this._translate.use(Lang);
  }
}
